import React, { useEffect, useRef, useState } from 'react';
import { Box, Container, Typography, Chip, Avatar } from '@mui/material';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import StarIcon from '@mui/icons-material/Star';

const testimonials = [
  {
    name: 'Marco Santos',
    role: 'Fleet Manager · LBC Express',
    avatar: 'MS',
    color: '#1075BA',
    quote: 'CarChecker cut our fuel costs by 22% in the first quarter. The driver behavior scores alone paid for the subscription — our teams drive safer now.',
    stars: 5,
  },
  {
    name: 'Diane Reyes',
    role: 'Operations Director · QuickFreight PH',
    avatar: 'DR',
    color: '#F8B220',
    quote: 'The geofencing alerts are a game changer. We know the moment a vehicle enters a restricted zone. Real-time peace of mind for our dispatch team.',
    stars: 5,
  },
  {
    name: 'Alvin Cruz',
    role: 'Owner · ARC Logistics',
    avatar: 'AC',
    color: '#8b5cf6',
    quote: "We've been able to onboard 3x more clients because we can now show them live vehicle locations. CarChecker made us look professional and reliable.",
    stars: 5,
  },
  {
    name: 'Patricia Lim',
    role: 'CEO · CoolBreeze Delivery',
    avatar: 'PL',
    color: '#10b981',
    quote: 'Issue reporting has been fantastic. Drivers report problems straight from their phones, we get notified instantly and dispatch maintenance. Zero downtime surprises.',
    stars: 5,
  },
  {
    name: 'Jerome Bautista',
    role: 'Transport Supervisor · DVLJ Corp',
    avatar: 'JB',
    color: '#ef4444',
    quote: 'Setup took less than 30 minutes for our 25-vehicle fleet. The dashboard is clean, intuitive, and shows exactly what we need. Best ROI we\'ve made this year.',
    stars: 5,
  },
  {
    name: 'Karen Ocampo',
    role: 'Head of Logistics · NovaMart',
    avatar: 'KO',
    color: '#1075BA',
    quote: 'The analytics reports save us hours every week. We used to compile driver reports manually — now it\'s automated with beautiful charts. Absolute must-have.',
    stars: 5,
  },
];

function TestimonialCard({ t, index }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold: 0.15 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        p: 3.5, borderRadius: '20px',
        background: '#fff',
        border: '1px solid rgba(16,117,186,0.1)',
        boxShadow: '0 4px 20px rgba(16,117,186,0.06)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'none' : 'translateY(24px)',
        transition: `all 0.65s ${index * 0.08}s ease`,
        '&:hover': { boxShadow: '0 16px 45px rgba(16,117,186,0.12)', transform: 'translateY(-4px)' },
        display: 'flex', flexDirection: 'column',
        breakInside: 'avoid',
        mb: 3,
      }}
    >
      <FormatQuoteIcon sx={{ color: `${t.color}30`, fontSize: 40, mb: 1, transform: 'scaleX(-1)' }} />

      <Box sx={{ display: 'flex', mb: 1 }}>
        {Array(t.stars).fill(0).map((_, i) => (
          <StarIcon key={i} sx={{ color: '#F8B220', fontSize: 16 }} />
        ))}
      </Box>

      <Typography sx={{ color: '#4a6580', lineHeight: 1.75, fontSize: '0.9rem', flex: 1, mb: 3 }}>
        "{t.quote}"
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar sx={{ bgcolor: t.color, fontWeight: 800, fontSize: '0.8rem', width: 40, height: 40 }}>{t.avatar}</Avatar>
        <Box>
          <Typography sx={{ fontWeight: 700, color: '#0d1f35', fontSize: '0.88rem' }}>{t.name}</Typography>
          <Typography sx={{ color: '#4a6580', fontSize: '0.75rem' }}>{t.role}</Typography>
        </Box>
      </Box>
    </Box>
  );
}

export default function Testimonials() {
  const [titleVisible, setTitleVisible] = useState(false);
  const titleRef = useRef();

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setTitleVisible(true); obs.disconnect(); } }, { threshold: 0.3 });
    if (titleRef.current) obs.observe(titleRef.current);
    return () => obs.disconnect();
  }, []);

  const col1 = testimonials.filter((_, i) => i % 3 === 0);
  const col2 = testimonials.filter((_, i) => i % 3 === 1);
  const col3 = testimonials.filter((_, i) => i % 3 === 2);

  return (
    <Box id="testimonials" sx={{ py: { xs: 10, md: 14 }, background: 'linear-gradient(160deg, #f5f9ff 0%, #eaf4ff 100%)', position: 'relative', overflow: 'hidden' }}>
      <Box sx={{ position: 'absolute', top: -100, left: -100, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(16,117,186,0.06) 0%, transparent 70%)', pointerEvents: 'none' }} />

      <Container maxWidth="lg">
        <Box ref={titleRef} sx={{ textAlign: 'center', mb: 8, opacity: titleVisible ? 1 : 0, transform: titleVisible ? 'none' : 'translateY(20px)', transition: 'all 0.7s ease' }}>
          <Chip label="⭐ Customer Stories" sx={{ mb: 2.5, bgcolor: 'rgba(248,178,32,0.12)', color: '#c88c00', fontWeight: 700, border: '1px solid rgba(248,178,32,0.3)' }} />
          <Typography variant="h2" sx={{ fontSize: { xs: '2.2rem', md: '3rem' }, color: '#0d1f35', mb: 2 }}>
            Loved by Fleet Managers{' '}
            <Box component="span" sx={{ background: 'linear-gradient(135deg, #1075BA, #F8B220)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
              Across the Philippines
            </Box>
          </Typography>
          <Typography sx={{ color: '#4a6580', fontSize: '1.05rem' }}>
            Join hundreds of businesses who trust CarChecker to keep their fleets safe and efficient.
          </Typography>
        </Box>

        {/* Masonry-style columns */}
        <Box sx={{ display: { xs: 'block', md: 'grid' }, gridTemplateColumns: '1fr 1fr 1fr', gap: 3 }}>
          {/* Mobile: show all in single col */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {testimonials.map((t, i) => <TestimonialCard key={t.name} t={t} index={i} />)}
          </Box>
          {/* Desktop masonry */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            {col1.map((t, i) => <TestimonialCard key={t.name} t={t} index={i} />)}
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'block' }, mt: 6 }}>
            {col2.map((t, i) => <TestimonialCard key={t.name} t={t} index={i + 2} />)}
          </Box>
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            {col3.map((t, i) => <TestimonialCard key={t.name} t={t} index={i + 4} />)}
          </Box>
        </Box>
      </Container>
    </Box>
  );
}
